import React, { useEffect, useMemo, useState, useCallback } from 'react';
import AntStandardPopup from '../AntStandardPopup';
import AntFileViewer from '../AntFileViewer';
import AntAPI from '../../utils/AntAPI';
import AntLocalization from '../../utils/AntLocalization';
import './styles.scss';

var t = function (obj) { return AntLocalization.translateText(obj); };

function fmtTime(v) {
    if (!v) return '';
    var n = typeof v === 'number' ? v : parseInt(v);
    if (!isNaN(n) && n > 0) {
        var d = new Date(n > 1e12 ? n : n * 1000);
        return d.toLocaleDateString() + ' · ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    try {
        var dd = new Date(v);
        return dd.toLocaleDateString() + ' · ' + dd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) { return String(v); }
}

function fmtSize(bytes) {
    var n = parseInt(bytes);
    if (!n) return '';
    if (n < 1024) return n + ' B';
    if (n < 1048576) return (n / 1024).toFixed(1) + ' KB';
    return (n / 1048576).toFixed(1) + ' MB';
}

function fileExt(name) {
    if (!name) return '';
    var dot = name.lastIndexOf('.');
    return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

function iconForExt(ext) {
    if (['pdf'].indexOf(ext) >= 0) return { glyph: 'PDF', color: '#e74c3c' };
    if (['doc', 'docx'].indexOf(ext) >= 0) return { glyph: 'DOC', color: '#2b6cb0' };
    if (['xls', 'xlsx', 'csv'].indexOf(ext) >= 0) return { glyph: 'XLS', color: '#2f855a' };
    if (['ppt', 'pptx'].indexOf(ext) >= 0) return { glyph: 'PPT', color: '#d35400' };
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].indexOf(ext) >= 0) return { glyph: 'IMG', color: '#6b46c1' };
    if (['mp4', 'mov', 'webm', 'mkv'].indexOf(ext) >= 0) return { glyph: 'VID', color: '#6b46c1' };
    if (['zip', 'rar', '7z'].indexOf(ext) >= 0) return { glyph: 'ZIP', color: '#4a5568' };
    return { glyph: 'FILE', color: '#718096' };
}

var STATUS_LABEL = {
    Draft: { vi: 'Nháp', en: 'Draft', fr: 'Brouill.', es: 'Borrador', id: 'Draf', ja: '下書き', zh: '草稿', ko: '초안', hi: 'ड्राफ़्ट', ar: 'مسودة' },
    Published: { vi: 'Đã ban hành', en: 'Published', fr: 'Publié', es: 'Publicado', id: 'Diterbitkan', ja: '発行済み', zh: '已发布', ko: '발행됨', hi: 'प्रकाशित', ar: 'منشور' },
    Archived: { vi: 'Lưu trữ', en: 'Archived', fr: 'Archivé', es: 'Archivado', id: 'Diarsipkan', ja: 'アーカイブ', zh: '已归档', ko: '보관됨', hi: 'संग्रहीत', ar: 'مؤرشف' }
};

/**
 * OFDocumentPreview — xem nội dung công văn (read-only).
 *
 * Layout dạng tài liệu user đọc:
 *   - Header: tiêu đề lớn + dòng meta (mã, người tạo, ngày, trạng thái)
 *   - Body: nội dung HTML (rich text)
 *   - Attachments: danh sách file đính kèm có thể click xem trực tiếp
 *
 * Props:
 *  - visible, onClose
 *  - doc: object record OFDocument (nếu có hiển thị luôn, vẫn fetch chi tiết để có description + attachments)
 *  - code: string code OFDocument
 */
export default function OFDocumentPreview(props) {
    var visible = props.visible;
    var onClose = props.onClose;
    var initialDoc = props.doc;
    var code = props.code || (initialDoc && initialDoc.code);

    var docAPI = useMemo(function () { return new AntAPI('of', 'OFDocument'); }, []);
    var attachAPI = useMemo(function () { return new AntAPI('media', 'MEDDocumentVoucher'); }, []);

    var [doc, setDoc] = useState(initialDoc || null);
    var [docLoading, setDocLoading] = useState(false);
    var [attachments, setAttachments] = useState([]);
    var [attachLoading, setAttachLoading] = useState(false);
    var [viewerFile, setViewerFile] = useState(null);

    useEffect(function () {
        setDoc(initialDoc || null);
    }, [initialDoc]);

    // Fetch chi tiết doc — luôn fetch khi visible để đảm bảo description + version mới nhất
    useEffect(function () {
        if (!visible || !code) return;
        setDocLoading(true);
        docAPI.post('/' + docAPI.routeURL + '/list', { code: code, pageSize: 1 }, function (result) {
            var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
            if (rows.length > 0) setDoc(rows[0]);
            setDocLoading(false);
        });
    }, [visible, code, docAPI]);

    // Fetch attachments từ MEDDocumentVoucher junction
    useEffect(function () {
        if (!visible || !code) return;
        setAttachLoading(true);
        attachAPI.post('/' + attachAPI.routeURL + '/list', {
            voucher_type: 'OFDocument',
            voucher_code: code,
            pageSize: 100,
            pageIndex: 0
        }, function (r) {
            var rows = (r && r.data && r.data.rows) || (r && r.rows) || [];
            var mapped = (Array.isArray(rows) ? rows : []).map(function (x) {
                return {
                    junction_id: x.id,
                    document_id: x.document_id,
                    document_code: x.document_code,
                    name: x.document_name,
                    sub_type: x.document_sub_type,
                    full_path: x.document_full_path,
                    short_path: x.document_short_path,
                    file_size: x.document_file_size || 0
                };
            });
            setAttachments(mapped);
            setAttachLoading(false);
        });
    }, [visible, code, attachAPI]);

    var openAttachment = useCallback(function (att) {
        if (!att.full_path) return;
        setViewerFile({
            name: att.name,
            url: att.full_path,
            type: att.sub_type || ''
        });
    }, []);

    if (!visible) return null;

    var statusKey = doc && doc.document_status;
    var statusLabel = STATUS_LABEL[statusKey] || (statusKey ? { vi: statusKey, en: statusKey } : null);

    return (
        <React.Fragment>
            <AntStandardPopup
                visible={visible}
                onClose={onClose}
                size="lg"
                contentPadding={false}
                actions={[
                    { label: { vi: 'Đóng', en: 'Close', fr: 'Fermer', es: 'Cerrar', id: 'Tutup', ja: '閉じる', zh: '关闭', ko: '닫기', hi: 'बंद करें', ar: 'إغلاق' }, variant: 'secondary', role: 'cancel' }
                ]}
            >
                <div className="ofdoc-preview">
                    {docLoading && !doc && (
                        <div className="ofdoc-loading">{t({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा...', ar: 'تحميل...' })}</div>
                    )}

                    {doc && (
                        <article className="ofdoc-paper">
                            {/* Header công văn */}
                            <header className="ofdoc-paper-head">
                                <h1 className="ofdoc-title">{doc.title || doc.code}</h1>
                                <div className="ofdoc-meta-line">
                                    {doc.code && <span className="ofdoc-code">{doc.code}</span>}
                                    {doc.folder_name && (
                                        <span className="ofdoc-folder">{doc.folder_name}</span>
                                    )}
                                    {statusLabel && (
                                        <span className={'ofdoc-status ofdoc-status--' + (statusKey || '').toLowerCase()}>
                                            {t(statusLabel)}
                                        </span>
                                    )}
                                </div>
                                <div className="ofdoc-byline">
                                    {doc.created_user_name && (
                                        <span>{t({ vi: 'Soạn bởi', en: 'By', fr: 'Par', es: 'Por', id: 'Oleh', ja: '作成者', zh: '起草人', ko: '작성자', hi: 'लेखक', ar: 'بواسطة' })} <strong>{doc.created_user_name}</strong></span>
                                    )}
                                    {doc.created_at && (
                                        <span>· {fmtTime(doc.created_at)}</span>
                                    )}
                                </div>
                            </header>

                            {/* Nội dung */}
                            <section className="ofdoc-body">
                                {doc.description ? (
                                    <div className="ofdoc-content ofdoc-rich"
                                         dangerouslySetInnerHTML={{ __html: doc.description }} />
                                ) : (
                                    <div className="ofdoc-empty">{t({ vi: 'Công văn chưa có nội dung', en: 'No content yet', fr: 'Aucun contenu', es: 'Sin contenido', id: 'Belum ada konten', ja: '内容がありません', zh: '暂无内容', ko: '내용 없음', hi: 'अभी कोई सामग्री नहीं', ar: 'لا يوجد محتوى بعد' })}</div>
                                )}
                            </section>

                            {/* Đính kèm */}
                            {(attachLoading || attachments.length > 0) && (
                                <section className="ofdoc-attach">
                                    <div className="ofdoc-attach-head">
                                        {t({ vi: 'Tệp đính kèm', en: 'Attachments', fr: 'Pièces jointes', es: 'Adjuntos', id: 'Lampiran', ja: '添付ファイル', zh: '附件', ko: '첨부 파일', hi: 'संलग्नक', ar: 'المرفقات' })}
                                        {attachments.length > 0 && <span className="ofdoc-attach-count">({attachments.length})</span>}
                                    </div>
                                    {attachLoading && attachments.length === 0 && (
                                        <div className="ofdoc-attach-loading">{t({ vi: 'Đang tải tệp...', en: 'Loading files...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat file...', ja: 'ファイル読込中...', zh: '正在加载文件...', ko: '파일 불러오는 중...', hi: 'फ़ाइलें लोड...', ar: 'تحميل الملفات...' })}</div>
                                    )}
                                    {attachments.length > 0 && (
                                        <div className="ofdoc-attach-list">
                                            {attachments.map(function (att) {
                                                var ext = fileExt(att.name);
                                                var icn = iconForExt(ext);
                                                var clickable = !!att.full_path;
                                                return (
                                                    <div key={att.junction_id || att.document_code}
                                                         className={'ofdoc-att-item' + (clickable ? ' is-clickable' : '')}
                                                         onClick={clickable ? function () { openAttachment(att); } : undefined}
                                                         title={clickable ? t({ vi: 'Xem trước', en: 'Preview', fr: 'Aperçu', es: 'Vista previa', id: 'Pratinjau', ja: 'プレビュー', zh: '预览', ko: '미리보기', hi: 'पूर्वावलोकन', ar: 'معاينة' }) : ''}>
                                                        <div className="ofdoc-att-icon" style={{ background: icn.color }}>{icn.glyph}</div>
                                                        <div className="ofdoc-att-info">
                                                            <div className="ofdoc-att-name">{att.name}</div>
                                                            <div className="ofdoc-att-meta">
                                                                {ext && <span>{ext.toUpperCase()}</span>}
                                                                {att.file_size > 0 && <span>· {fmtSize(att.file_size)}</span>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </section>
                            )}
                        </article>
                    )}
                </div>
            </AntStandardPopup>

            {viewerFile && (
                <AntFileViewer
                    visible
                    files={[viewerFile]}
                    initialIndex={0}
                    onClose={function () { setViewerFile(null); }}
                />
            )}
        </React.Fragment>
    );
}
